export const SITE_ORIGIN = 'https://www.flymeta.com'
export const SITE_NAME = 'Fly Meta'
export const ORGANIZATION_ID = `${SITE_ORIGIN}/#organization`
export const WEBSITE_ID = `${SITE_ORIGIN}/#website`
export const SITE_LOGO_URL = `${SITE_ORIGIN}/favicon.png`

export const SOCIAL_PROFILE_URLS = [
  'https://x.com/fly_meta',
  'https://www.linkedin.com/company/fly-meta/',
  'https://www.instagram.com/fly_meta'
]

export const DEFAULT_DESCRIPTION = 'Fly Meta provides international air cargo charter, aircraft asset management, flight operations support and aviation material trading services.'

export const ROUTE_SEO = {
  Index: {
    title: 'Fly Meta | Air Cargo Charter & Aviation Services',
    description: DEFAULT_DESCRIPTION
  },
  About: {
    pageTitle: 'About',
    description: 'Learn about Fly Meta, an international aviation services company focused on air cargo charter, freighter operations and global logistics support.'
  },
  Services: {
    pageTitle: 'Services',
    description: 'Explore Fly Meta aviation services including cargo charter operations, aircraft asset management, flight support and aviation material trading.'
  },
  AircraftAssetManagement: {
    pageTitle: 'Aircraft Asset Management',
    description: 'Fly Meta supports aircraft asset planning, lease coordination and lifecycle management for cargo operators and aviation investors worldwide.'
  },
  CharterOperations: {
    pageTitle: 'Charter Operations',
    description: 'Fly Meta delivers responsive air cargo charter solutions for time-critical freight, oversized cargo and complex international logistics requirements.'
  },
  FlightOperationsSupport: {
    pageTitle: 'Flight Operations Support',
    description: 'Fly Meta provides flight operations support for cargo missions, coordinating operational resources across routes, airports and regulatory environments.'
  },
  AviationMaterialTrading: {
    pageTitle: 'Aviation Material Trading',
    description: 'Fly Meta connects aviation material trading resources for aircraft parts, components and supply support across the global cargo aviation market.'
  },
  Fleet: {
    pageTitle: 'Fleet',
    description: 'Review Fly Meta freighter fleet capabilities, including wide-body cargo aircraft designed for long-haul international air freight missions.'
  },
  'B747-400BDSF': {
    pageTitle: 'B747-400BDSF',
    description: 'Explore B747-400BDSF freighter capabilities for high-volume cargo transport, flexible loading configurations and long-range international operations.'
  },
  'B747-400F': {
    pageTitle: 'B747-400F',
    description: 'Explore B747-400F freighter specifications for heavy cargo capacity, main deck loading and reliable long-haul air freight operations.'
  },
  'B777-300ERSF': {
    pageTitle: 'B777-300ERSF',
    description: 'Explore B777-300ERSF cargo aircraft capabilities, offering efficient long-range freight performance for modern international logistics networks.'
  },
  Article: {
    pageTitle: 'News Article',
    description: 'Read Fly Meta news and aviation updates covering air cargo charter, fleet operations, industry insights and company announcements.',
    type: 'article'
  },
  News: {
    pageTitle: 'News',
    description: 'Read Fly Meta news, company updates and aviation industry insights related to air cargo, freighter fleets and international logistics.'
  },
  Careers: {
    pageTitle: 'Careers',
    description: 'Discover Fly Meta career opportunities for aviation professionals interested in air cargo operations, logistics services and global growth.'
  },
  Investors: {
    pageTitle: 'Investors',
    description: 'Find Fly Meta investor information, company development updates and aviation market perspectives for partners and stakeholders.'
  },
  Contact: {
    pageTitle: 'Contact',
    description: 'Contact Fly Meta for air cargo charter inquiries, aviation service requests, fleet support and international business cooperation.'
  },
  NotFound: {
    pageTitle: 'Page Not Found',
    description: 'The requested Fly Meta page could not be found. Explore cargo charter, fleet, news and contact information from the home page.'
  }
}

export const buildSeoTitle = seo => {
  if (seo && seo.title) return seo.title
  if (seo && seo.pageTitle) return `${seo.pageTitle} | ${SITE_NAME}`
  return ROUTE_SEO.Index.title
}

export const buildCanonicalUrl = route => {
  const path = route && route.path ? route.path : '/en/'
  return `${SITE_ORIGIN}${path}`
}

export const buildLocalizedUrl = (locale, path = '/') => {
  const nextLocale = locale || 'en'
  const rawPath = String(path || '/')
  const nextPath = rawPath === '/' ? '/' : `/${rawPath.replace(/^\/+|\/+$/g, '')}`
  return `${SITE_ORIGIN}/${nextLocale}${nextPath === '/' ? '/' : nextPath}`
}

export const buildSeoMetaInfo = (seo, route) => {
  const nextSeo = seo || ROUTE_SEO.Index
  const title = buildSeoTitle(nextSeo)
  const description = nextSeo.description || DEFAULT_DESCRIPTION
  const url = nextSeo.canonical || buildCanonicalUrl(route)
  const type = nextSeo.type || 'website'
  const lang = route && route.params && route.params.locale ? route.params.locale : 'en'

  return {
    title,
    htmlAttrs: {
      lang
    },
    meta: [
      { vmid: 'description', name: 'description', content: description },
      { vmid: 'og:title', property: 'og:title', content: title },
      { vmid: 'og:description', property: 'og:description', content: description },
      { vmid: 'og:type', property: 'og:type', content: type },
      { vmid: 'og:url', property: 'og:url', content: url }
    ],
    link: [
      { vmid: 'canonical', rel: 'canonical', href: url }
    ]
  }
}

export const stripHtml = value => String(value || '')
  .replace(/<script[\s\S]*?<\/script>/gi, ' ')
  .replace(/<style[\s\S]*?<\/style>/gi, ' ')
  .replace(/<[^>]+>/g, ' ')
  .replace(/&nbsp;|&#160;/g, ' ')
  .replace(/&amp;/g, '&')
  .replace(/&lt;/g, '<')
  .replace(/&gt;/g, '>')
  .replace(/&quot;/g, '"')
  .replace(/&#39;/g, "'")
  .replace(/\s+/g, ' ')
  .trim()

export const truncateSeoText = (value, maxLength = 160) => {
  const text = stripHtml(value)
  if (text.length <= maxLength) return text
  return `${text.slice(0, maxLength - 3).trim()}...`
}

export const createJsonLdScript = (vmid, json) => ({
  vmid,
  type: 'application/ld+json',
  json
})

export const buildOrganizationJsonLd = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': ORGANIZATION_ID,
  name: SITE_NAME,
  url: SITE_ORIGIN,
  logo: SITE_LOGO_URL,
  description: DEFAULT_DESCRIPTION,
  sameAs: SOCIAL_PROFILE_URLS
})

export const buildWebSiteJsonLd = locale => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': WEBSITE_ID,
  name: SITE_NAME,
  url: buildLocalizedUrl(locale, '/'),
  inLanguage: locale || 'en',
  publisher: {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: SITE_NAME
  }
})

export const buildBreadcrumbJsonLd = items => {
  const itemList = (items || []).filter(item => item && item.name && item.url)
  if (!itemList.length) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: itemList.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: item.url
    }))
  }
}

export const buildAggregatePageBreadcrumbJsonLd = ({ locale, homeName = 'Home', pageName, path } = {}) => {
  if (!pageName || !path) return null

  return buildBreadcrumbJsonLd([
    { name: homeName, url: buildLocalizedUrl(locale, '/') },
    { name: pageName, url: buildLocalizedUrl(locale, path) }
  ])
}

export const buildServiceJsonLd = ({ name, description, url } = {}) => {
  if (!name || !url) return null

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    serviceType: name,
    description: truncateSeoText(description || DEFAULT_DESCRIPTION, 300),
    url,
    provider: {
      '@type': 'Organization',
      '@id': ORGANIZATION_ID,
      name: SITE_NAME
    }
  }
}

export const toIsoDateTime = value => {
  if (!value) return undefined
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return date.toISOString()
}

export const buildArticleJsonLd = ({ headline, description, datePublished, dateModified, image, url } = {}) => {
  if (!headline || !url) return null

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'NewsArticle',
    headline,
    author: {
      '@type': 'Organization',
      name: SITE_NAME
    },
    publisher: {
      '@type': 'Organization',
      '@id': ORGANIZATION_ID,
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: SITE_LOGO_URL
      }
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': url
    }
  }

  const nextDescription = truncateSeoText(description || '', 300)
  if (nextDescription) schema.description = nextDescription
  if (datePublished) schema.datePublished = toIsoDateTime(datePublished)
  if (dateModified) schema.dateModified = toIsoDateTime(dateModified)
  if (image) schema.image = [image]

  return schema
}
